import type { ChangeEvent, Dispatch, RefObject, SetStateAction } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { CdropdownOption } from '../Cdropdown';
import { TextInputMode, type CtextinputElement } from './Ctextinput.types';

interface UseCtextinputInteractionsInput {
  availableOptions: string[];
  disabled: boolean;
  mode: TextInputMode;
  onBlur?: () => void;
  onChange: (value: string) => void;
  onFocus?: () => void;
}

interface CtextinputInteractions {
  dropdownOptions: CdropdownOption[];
  handleBlur: () => void;
  handleContainerClick: () => void;
  handleFocus: () => void;
  handleInputChange: (event: ChangeEvent<CtextinputElement>) => void;
  inputRef: RefObject<CtextinputElement | null>;
  isDropdownOpen: boolean;
  isFocused: boolean;
  setIsDropdownOpen: Dispatch<SetStateAction<boolean>>;
}

export function useCtextinputInteractions({
  availableOptions,
  disabled,
  mode,
  onBlur,
  onChange,
  onFocus,
}: UseCtextinputInteractionsInput): CtextinputInteractions {
  const [isFocused, setIsFocused] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const inputRef = useRef<CtextinputElement>(null);

  const handleFocus = useCallback((): void => {
    if (!disabled) {
      setIsFocused(true);
    }
    onFocus?.();
  }, [disabled, onFocus]);

  const handleBlur = useCallback((): void => {
    setIsFocused(false);
    onBlur?.();
  }, [onBlur]);

  const handleInputChange = useCallback(
    (event: ChangeEvent<CtextinputElement>): void => {
      if (!disabled) {
        onChange(event.target.value);
      }
    },
    [disabled, onChange],
  );

  const handleContainerClick = useCallback((): void => {
    if (disabled || mode === TextInputMode.Dropdown) {
      return;
    }
    inputRef.current?.focus();
  }, [disabled, mode]);

  const dropdownOptions = useMemo<CdropdownOption[]>(
    () =>
      availableOptions.map((option) => ({
        key: option,
        label: option,
        onClick: () => onChange(option),
      })),
    [availableOptions, onChange],
  );

  return {
    dropdownOptions,
    handleBlur,
    handleContainerClick,
    handleFocus,
    handleInputChange,
    inputRef,
    isDropdownOpen,
    isFocused,
    setIsDropdownOpen,
  };
}
